<template>
  <div>
    <div class="container main-search" :class="{ 'no-scroll': show }" id="head">
      <h1 class="intro-text">Znajdź usterkę</h1>
      <p class="brand-color site-description">
        Wyszukaj i wybierz objawy które występują u Ciebie, a my podpowiemy jaka
        usterka może je wywoływać.
      </p>

      <p ref="search-form" class="font-24"><strong>Co się dzieje?</strong></p>
      <div class="row selected-symptoms">
        <div>
          <p
            v-for="symptom in selectedSymptoms"
            :key="symptom.id"
            @click="deselectSymptom(symptom)"
          >
            <strong class="brand-color"
              >{{ symptom.name }} <b-icon-x-circle
            /></strong>
          </p>
        </div>
      </div>

      <!-- Search form -->
      <div class="row mb-2">
        <div class="col">
          <input
            v-oninput="searchPhrase"
            @focus="moveInputToTop"
            class="form-control"
            name="search"
            type="search"
            placeholder="Wyszukaj po objawach..."
          />
        </div>
      </div>

      <div class="row" v-if="isLoading">
        <div class="d-flex justify-content-center">
          <Loading />
        </div>
      </div>
      <div class="row" v-else-if="symptoms.length > 0">
        <p></p>
        <strong
          >wyniki dla: <u>{{ searchPhrase }}</u></strong
        >
        <ul class="list-group list-group-flush">
          <li
            class="list-group-item symptom"
            v-for="symptom in symptoms"
            :key="symptom.id"
            @click="selectSymptom(symptom)"
          >
            {{ symptom.name }}
          </li>
        </ul>
        <div class="mb-5">
          <mail-to-us
            bold-text="Brakuje twoich objawów na liście?"
            small-text="Prześlij nam informację"
            :type="FormTypes.SYMPTOMS"
            v-if="symptoms"
          />
        </div>
      </div>
      <div class="row" v-else-if="searchPhrase && searchPhrase.length < 3">
        <p></p>
        <p>Musisz wpisać co najmniej 3 litery by wyszukać.</p>
      </div>
      <div class="row" v-else-if="notFound">
        <p></p>
        <p class="link-danger">Nie znaleziono.</p>
      </div>

      <div class="row" v-if="otherSymptoms.length >= 1 && faults.length > 1">
        <div class="header symptom-header">
          <strong>Czy występują jeszcze inne objawy?</strong>
        </div>
        <ul class="list-group list-group-flush">
          <li
            class="list-group-item text-gray symptom"
            @click="selectSymptom(symptom)"
            v-for="symptom in otherSymptoms"
            :key="symptom.id"
          >
            {{ symptom.name }}
          </li>
        </ul>
      </div>
    </div>

    <div
      class="fixed-bottom find-faults"
      :class="{ show: show }"
      v-if="faults.length > 0"
      ref="find-faults"
    >
      <div class="row bg-brand header" @click="toggleFaults">
        <div>
          {{ faults.length }} potencjalne przyczyny &nbsp; &nbsp;
          <button class="btn btn-sm btn-light">{{ showText }}</button>
        </div>
      </div>
      <div class="content pt-4 scrollable">
        <div class="container force-mobile">
          <div class="row">
            <div class="col-12">
              <div
                v-for="fault in faults"
                :key="fault.id"
                class="pt-3 pb-3 font-13"
              >
                <router-link
                  :to="{ name: 'fault-detail', params: { faultId: fault.id } }"
                  class="text-decoration-none"
                >
                  <span class="font-18 text-black">{{ fault.name }}</span>
                  <br />
                  <ul>
                    <li
                      :class="{
                        'selected-symptom': symptomIsSelected(symptom),
                        'text-secondary': !symptomIsSelected(symptom),
                      }"
                      v-for="symptom in sortedBySelected(fault.symptoms)"
                      :key="symptom.id"
                    >
                      {{ symptom.name }}
                    </li>
                  </ul>
                  <div class="d-flex justify-content-end">
                    <u>zobacz opis usterki <note-icon /></u>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
          <div class="return-to-symptoms" @click="toggleFaults">
            <return-arrow /> <u>zmień listę objawów</u>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Loading from "@/components/Loading";
import { BIconXCircle } from "bootstrap-icons-vue";
import debounce from "debounce";
import ReturnArrow from "@/components/icons/ReturnArrow";
import NoteIcon from "@/components/icons/NoteIcon";
import MailToUs from "@/improvementForms/MailToUs";
import { FormTypes } from "@/improvementForms/formTypes";
import { mapGetters, mapMutations } from "vuex";

const oninput = {
  created(el, binding) {
    el.oninput = () => {
      binding.instance.searchPhrase = el.value;
    };
  },
};

export default {
  data: function () {
    return {
      searchPhrase: "",
      symptoms: [],
      isLoading: false,
      show: false,
      notFound: false,
      selectedSymptoms: [],
      faults: [],
      FormTypes,
    };
  },
  created() {
    this.searchSymptoms = debounce(this.searchSymptoms, 300);
  },
  mounted() {
    if (this.getInitObject.object) {
      this.selectedSymptoms = this.getInitObject.object;
      this.findFaults();
      this.resetInitObject();
    } else {
      const initialSymptomIdList = this.$route.query.q;
      document.title = "Znajdź usterkę po objawach";
      if (initialSymptomIdList) {
        this.isLoading = true;
        let q = initialSymptomIdList;
        if (initialSymptomIdList instanceof Array) {
          q = initialSymptomIdList.join("&q=");
        }
        axios.get(`/api/faults/symptoms/?q=${q}`).then((response) => {
          this.selectedSymptoms = response.data;
          this.findFaults();
          this.isLoading = false;
        });
      }
    }
  },
  methods: {
    ...mapMutations(["resetInitObject"]),
    searchSymptoms() {
      let searchUrl = this.$route.query.disableElastic
        ? `/api/faults/symptoms?s=${this.searchPhrase}`
        : `/api/faults/symptoms/search?q=${this.searchPhrase}`;

      if (this.searchPhrase.length >= 3) {
        this.isLoading = true;
        for (const symptom of this.selectedSymptoms) {
          searchUrl += `&exclude_ids=${symptom.id}`;
        }
        axios.get(searchUrl).then((response) => {
          this.symptoms = response.data;
          this.isLoading = false;
          if (this.symptoms.length === 0) {
            this.notFound = true;
          }
        });
      } else {
        this.symptoms = [];
        this.notFound = false;
      }
    },
    selectSymptom(symptom) {
      this.selectedSymptoms.push(symptom);
      this.pushSelectedSymptomsToQuery();
      // solution 1 - easiest
      const index = this.symptoms.findIndex((s) => s.id === symptom.id);
      this.symptoms.splice(index, 1);
      // solution 2 - "visual 'lag'"
      // this.searchSymptoms();
      this.findFaults();
    },
    deselectSymptom(symptom) {
      const index = this.selectedSymptoms.findIndex((s) => s.id === symptom.id);
      this.selectedSymptoms.splice(index, 1);
      this.pushSelectedSymptomsToQuery();
      this.findFaults();

      this.searchSymptoms();
    },
    pushSelectedSymptomsToQuery() {
      this.$router.push({
        query: { q: this.selectedSymptoms.map((symptom) => symptom.id) },
      });
    },
    findFaults() {
      if (this.selectedSymptoms <= 0) {
        this.faults = [];
      } else {
        let url = "/api/faults/faults_with_symptoms/?";
        for (let symptom of this.selectedSymptoms) {
          url += `symptoms=${symptom.id}&`;
        }
        axios.get(url).then((response) => {
          this.faults = response.data;
        });
      }
    },
    toggleFaults() {
      this.show = !this.show;
    },
    moveInputToTop() {
      window.scrollTo(0, this.$refs["search-form"].offsetTop);
    },
    symptomIsSelected(symptom) {
      return this.selectedSymptoms.findIndex((s) => s.id === symptom.id) >= 0;
    },
    sortedBySelected(symptoms) {
      return [...symptoms].sort((s1) => {
        if (this.symptomIsSelected(s1)) {
          return -1;
        }
        return 1;
      });
    },
  },
  computed: {
    ...mapGetters(["getInitObject"]),
    otherSymptoms() {
      if (this.faults.length <= 0) {
        return [];
      } else {
        const otherSymptoms = [];
        for (let fault of this.faults) {
          for (let symptom of fault.symptoms) {
            if (
              this.selectedSymptoms.findIndex((s) => s.id === symptom.id) < 0
            ) {
              otherSymptoms.push(symptom);
            }
          }
        }
        return otherSymptoms;
      }
    },
    showText() {
      return this.show ? "SCHOWAJ" : "POKAŻ";
    },
  },
  watch: {
    searchPhrase() {
      this.searchSymptoms();
    },
  },
  directives: {
    oninput,
  },
  components: {
    MailToUs,
    NoteIcon,
    ReturnArrow,
    Loading,
    BIconXCircle,
  },
};
</script>

<style scoped>
.head-text {
  font-size: calc(1.925rem + 0.9vw);
}

.brand-color {
  color: #0072db;
}

.site-description {
  font-size: 16px;
}

.selected-symptoms {
  margin-top: 1em;
}

.find-faults .header {
  text-align: center;
  align-items: center;
  height: 4rem;
}

.find-faults {
  height: 1%;
  min-height: 64px;
  background-color: white;
  transition: height 0.5s cubic-bezier(0.39, 0.58, 0.57, 1);
}

.find-faults.show {
  height: 100%;
}

.font-18 {
  font-size: 18px;
}

.font-13 {
  font-size: 13px;
}

.font-24 {
  font-size: 24px;
}

.selected-symptom {
  color: var(--brand-color);
  font-weight: bold;
}

.symptom-header {
  background-color: AliceBlue;
  text-align: center;
  padding: 1rem 0rem 1rem 0rem;
  margin: 2rem 0 1rem 0;
}

.text-gray {
  color: gray;
}

.main-search {
  min-height: 90%;
}

.no-scroll {
  overflow: hidden;
  height: 80%;
  min-height: 0;
}

.scrollable {
  height: 100%;
  overflow: scroll;
  padding-bottom: 10vh;
  margin-left: auto;
  margin-right: auto;
}

.return-to-symptoms {
  margin-left: 20px;
  margin-top: 30px;
  margin-bottom: 30px;
  cursor: pointer;
  color: #aaaaaa;
}

.symptom {
  position: static;
}
</style>
